import { FC, ReactNode } from 'react';
import { IconV2, IconV2List, ComposableTable as TableComponents, Tooltip } from '@abtasty/design-system';
import { Density } from '@abtasty/design-system/dist/components/ComposableTable/types';
import { useIsMobile } from '@src/application/hooks/useIsMobile';

import { Desktop } from '@src/application/modules/shared/components/MediaQuery/MediaQuery';
import { Cell, EmptyTableMessage, HeaderRow, HeaderWrapper } from './TableStyledComponents';
import { TableHeader } from './TableHeader';
import { useTheme } from 'styled-components';
import { ColumnHeader } from '../../type';

const { ComposableTable, EmptyTable } = TableComponents;

type TableProps = {
  title?: string;
  headers: ColumnHeader[];
  children: ReactNode;
  headerComponent?: ReactNode;
  withBorderRow?: boolean;
  hideHeaderRow?: boolean;
};

export const Table: FC<TableProps> = ({
  title,
  headers,
  children,
  headerComponent,
  withBorderRow = true,
  hideHeaderRow = false,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  return (
    <ComposableTable
      withoutBorders={!withBorderRow}
      heightDensity={(isMobile ? 'comfortable' : 'default') as Density}
      withoutShadow
    >
      <Desktop>
        <HeaderWrapper>
          {(title || headerComponent) && <TableHeader title={title}>{headerComponent}</TableHeader>}

          {!hideHeaderRow && (
            <HeaderRow>
              {headers.map((h, i) => (
                <Cell key={`row-header-${i}`} width={h.width} align={h.align}>
                  {h.label}
                  {h.info && (
                    <Tooltip content={h.info} placement="top">
                      <IconV2 icon={IconV2List.info} color={theme.dec.color.primary.default.fg.subbtle} size={16} />
                    </Tooltip>
                  )}
                </Cell>
              ))}
            </HeaderRow>
          )}
        </HeaderWrapper>
      </Desktop>
      {children}
    </ComposableTable>
  );
};

type TableEmptyProps = { noDataMessage?: string };

export const TableEmpty: FC<TableEmptyProps> = ({ noDataMessage }) => {
  return (
    <EmptyTable illustrationName="embarrassedAli" dataTestId="composable-table-empty">
      <EmptyTableMessage>
        Oops!
        <br />
        {noDataMessage}
      </EmptyTableMessage>
    </EmptyTable>
  );
};
